import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback } from 'react';
import { Layout, Model, type TabNode, type IJsonModel, Actions, DockLocation } from 'flexlayout-react';
import { Bot, Settings, PanelRight, Loader2 } from 'lucide-react';
import { useProjectStore } from '../stores/projectStore';
import { useMachineStore } from '../stores/machineStore';
import { useLayoutStore } from '../stores/layoutStore';
import { useUIStore } from '../stores/uiStore';
import { useRdpStore } from '../stores/rdpStore';
import { useMobile } from '../hooks/useMobile';
import { DEFAULT_LAYOUT } from '../layouts/defaultLayout';
import { getTab, getAddableTabs, getMobileTabs } from '../registries/tabRegistry';
import type { Project } from '../services/backend/types';
import { isWeb } from '../lib/platform';
import { RDP_ENABLED } from '../config/features';
import { logger } from '@/lib/logger';
import MobileHeader from '../components/workspace/MobileHeader';
import RunButton from '../components/workspace/RunButton';

// Mobile fallback with simple tabs
function MobileWorkspace({ projectId, project }: { projectId?: string; project?: Project | null }) {
  const tabs = getMobileTabs();
  const location = useLocation();
  // Honor `?tab=<id>` so other surfaces (e.g. clickable transcription toast)
  // can deep-link straight to a particular workspace tab. The `?t=` cache
  // buster the link includes makes search reactive even when the same tab
  // is requested twice in a row.
  const initialTab =
    new URLSearchParams(location.search).get('tab') || tabs[0]?.id || 'terminal';
  const [activeTabId, setActiveTabId] = useState(initialTab);
  useEffect(() => {
    const t = new URLSearchParams(location.search).get('tab');
    if (t && tabs.some((tab) => tab.id === t)) {
      setActiveTabId(t);
    }
  }, [location.search, tabs]);
  const openSettings = useUIStore((state) => state.openSettings);

  const activeTab = getTab(activeTabId);

  return (
    <div className="flex flex-col h-full">
      {/* Mobile header: top-level project switcher, run, settings, user */}
      <MobileHeader
        projectPath={project?.root_path}
        onSettingsOpen={openSettings}
      />

      {/* Active panel fills the space between header and bottom nav. Only the
          active panel is mounted (lighter on a phone — the OSR browser, PTY and
          queue are heavy); per-panel state survives via their stores. */}
      <div className="flex-1 overflow-hidden">
        {activeTab ? (() => {
          const Component = activeTab.component;
          return <Component {...activeTab.propsFactory({ projectId, project })} />;
        })() : (
          <div className="p-4 text-base-content/60">Select a panel.</div>
        )}
      </div>

      {/* Bottom navigation bar — thumb-reachable, safe-area aware (iOS home
          indicator). The native mobile pattern: icons + short labels. */}
      <nav
        className="flex shrink-0 border-t border-base-300 bg-base-200"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTabId === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTabId(tab.id)}
              aria-current={isActive ? 'page' : undefined}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 text-[10px] font-medium transition-colors ${
                isActive ? 'text-primary' : 'text-base-content/55 hover:text-base-content/80'
              }`}
            >
              <Icon className={`w-5 h-5 ${isActive ? '' : 'opacity-90'}`} />
              {tab.displayName}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

export default function DockingWorkspace() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { layoutJson, setLayoutJson, resetCounter } = useLayoutStore();
  const openSettings = useUIStore((state) => state.openSettings);

  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Retry trigger: on desktop the local core IPC can still be connecting when the
  // workspace mounts, so the first getProject() throws "IPC not connected" and the
  // project stays null — leaving the workspace stuck on the connecting spinner with
  // no dep change to retry. CefBackend fires `ac:core-reconnected` on every (re)open
  // (including the first successful connect); bump this to re-run the load then, so
  // the workspace recovers on its own instead of needing "Cancel & switch machine".
  const [coreReadyTick, setCoreReadyTick] = useState(0);
  // Track whether we've done the initial project load — prevents workspace unmount on reconnection
  const initialLoadDoneRef = useRef(false);
  // Once the workspace has rendered its Layout at least once this session, never
  // unmount it again for a project switch (or a brief reconnect). Unmounting the
  // Layout tears down EVERY panel — including the global Chat panel's host
  // browsers — which hides + recreates them and resets the session. So the
  // full-screen spinner is for the FIRST load only; later project switches keep
  // the Layout mounted and just swap each panel's projectId underneath.
  const everReadyRef = useRef(false);
  const lastProjectIdRef = useRef(id);
  if (id !== lastProjectIdRef.current) {
    lastProjectIdRef.current = id;
    initialLoadDoneRef.current = false;
  }

  const getProject = useProjectStore((state) => state.getProject);
  const setCurrentProject = useProjectStore((state) => state.setCurrentProject);
  // Subscribe to the live project record so settings edits (default agent,
  // run configs, etc.) propagate into child tabs without a project switch.
  // Local `project` state is still the source of truth during the initial
  // load (so we can show "Loading..." until getProject resolves), but once
  // it's loaded we prefer the store value when its id matches.
  const liveCurrentProject = useProjectStore((state) => state.currentProject);
  const connectionState = useMachineStore((state) => state.connectionState);
  const webrtcReady = useMachineStore((state) => state.webrtcReady);
  const webrtcConnecting = useMachineStore((state) => state.webrtcConnecting);
  const webMode = isWeb();

  // Build the model from stored JSON
  const modelRef = useRef<Model | null>(null);
  const lastResetRef = useRef(resetCounter);
  if (!modelRef.current || lastResetRef.current !== resetCounter) {
    lastResetRef.current = resetCounter;
    try {
      modelRef.current = Model.fromJson(layoutJson);
    } catch {
      // If stored layout is corrupt, reset to defaults
      modelRef.current = Model.fromJson(DEFAULT_LAYOUT);
    }
  }

  // Auto-add RDP panel when viewer starts (e.g. from MachineSelector RDP button)
  const rdpIsViewing = useRdpStore((s) => s.isViewing);
  useEffect(() => {
    if (!RDP_ENABLED || !rdpIsViewing || !modelRef.current) return;
    // Check if an RDP tab already exists in the layout
    const root = modelRef.current.getRoot();
    let rdpExists = false;
    modelRef.current.visitNodes((node) => {
      if (node.getType() === 'tab' && (node as TabNode).getComponent() === 'rdp') {
        rdpExists = true;
      }
    });
    if (!rdpExists) {
      modelRef.current.doAction(
        Actions.addNode(
          { type: 'tab', component: 'rdp', name: 'Remote Desktop', id: `rdp-${Date.now()}` },
          root.getId(),
          DockLocation.CENTER,
          -1,
          true
        )
      );
    }
  }, [rdpIsViewing]);

  // Load project data — retries when connection state changes
  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    // In web mode, wait for WebRTC only on initial load.
    // After the project is loaded, keep the workspace mounted even if WebRTC
    // briefly disconnects (e.g., reconnection) to avoid losing ChatTab state.
    if (webMode && !webrtcReady && !initialLoadDoneRef.current) {
      setIsLoading(true);
      return;
    }

    // Desktop mode: local projects load from the filesystem — don't block on
    // server auth. Server connection is optional (used for incoming WebRTC).

    const loadProject = async () => {
      try {
        // Only show full loading spinner on initial load — reloads happen silently
        // to avoid unmounting the workspace (which kills ChatTab session state)
        if (!initialLoadDoneRef.current) {
          setIsLoading(true);
        }
        await setCurrentProject(id);
        const projectData = await getProject(id);
        setProject(projectData || null);
        initialLoadDoneRef.current = true;
      } catch (error) {
        logger.error('DockingWorkspace', 'Failed to load project:', error);
        // Only clear project on initial load failure
        if (!initialLoadDoneRef.current) {
          setProject(null);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadProject();
  }, [id, getProject, setCurrentProject, connectionState, webrtcReady, webMode, coreReadyTick]);

  // Retry the project load once the local core IPC (re)connects — see coreReadyTick.
  useEffect(() => {
    const onCoreReady = () => setCoreReadyTick((t) => t + 1);
    window.addEventListener('ac:core-reconnected', onCoreReady);
    return () => window.removeEventListener('ac:core-reconnected', onCoreReady);
  }, []);

  // Source the project for child tabs from the store when its id matches —
  // that keeps Settings edits (default agent, runner configs) live without
  // forcing a project switch. Falls back to the local state during the
  // initial load before the store has caught up.
  const projectForTabs = liveCurrentProject?.id === id ? liveCurrentProject : project;

  // Tab factory — maps component names to React components
  const factory = useCallback(
    (node: TabNode) => {
      const tabDef = getTab(node.getComponent());
      if (!tabDef) return <div className="p-4 text-base-content/60">Unknown panel: {node.getComponent()}</div>;
      const Component = tabDef.component;
      return <Component {...tabDef.propsFactory({ projectId: id, project: projectForTabs })} />;
    },
    [id, projectForTabs]
  );

  // Persist layout changes
  const handleModelChange = useCallback(
    (model: Model) => {
      setLayoutJson(model.toJson() as IJsonModel);
    },
    [setLayoutJson]
  );

  // Custom tab rendering with icons
  const onRenderTab = useCallback(
    (node: TabNode, renderValues: { leading: React.ReactNode; content: React.ReactNode }) => {
      const tabDef = getTab(node.getComponent());
      if (tabDef) {
        const Icon = tabDef.icon;
        renderValues.leading = <Icon className="w-3.5 h-3.5 mr-1.5" />;
      }
    },
    []
  );

  // Add a panel that was previously closed
  const addPanel = (component: string, name: string) => {
    if (!modelRef.current) return;
    modelRef.current.doAction(
      Actions.addNode(
        { type: 'tab', component, name, id: `${component}-${Date.now()}` },
        modelRef.current.getRoot().getId(),
        DockLocation.CENTER,
        -1,
        true
      )
    );
  };

  // Agent-open bridge: the MCP browser_open tool evals
  // `window.__atelierAgentOpenBrowser(url)` here. If the Browser panel is already
  // mounted, open the tab immediately and return its webview label. If not, add
  // the panel and stash the url for BrowserTab to drain on mount — returns '' so
  // the agent knows to re-call (browser_open/browser_list) once the panel is up.
  useEffect(() => {
    const w = window as unknown as {
      __atelierAgentOpenBrowser?: (url?: string) => string;
      __atelierOpenBrowserTab?: (url?: string) => string;
      __atelierPendingBrowserOpen?: string;
    };
    const fn = (url = ''): string => {
      if (typeof w.__atelierOpenBrowserTab === 'function') return w.__atelierOpenBrowserTab(url);
      // Panel not mounted. Stash the url for BrowserTab to drain on mount, then
      // make sure a Browser panel is mounted+active so it actually renders:
      // FlexLayout mounts a tab's component lazily on select, so an existing but
      // inactive browser node won't run BrowserTab's effect. Select it if it
      // exists; otherwise add one (addNode selects it).
      w.__atelierPendingBrowserOpen = url;
      let browserNodeId: string | null = null;
      modelRef.current?.visitNodes((node) => {
        if ((node as { getComponent?: () => string }).getComponent?.() === 'browser') {
          browserNodeId = node.getId();
        }
      });
      if (browserNodeId) {
        modelRef.current?.doAction(Actions.selectTab(browserNodeId));
      } else {
        addPanel('browser', 'Browser');
      }
      return '';
    };
    w.__atelierAgentOpenBrowser = fn;
    return () => {
      if (w.__atelierAgentOpenBrowser === fn) delete w.__atelierAgentOpenBrowser;
    };
  }, []);

  const isConnectionReady = webMode ? webrtcReady : connectionState === 'authenticated';

  // In web mode: if authenticated but WebRTC failed (not connecting, not ready), show reconnect option
  if (webMode && connectionState === 'authenticated' && !webrtcReady && !webrtcConnecting && !project) {
    const handleRetry = async () => {
      const { selectedMachineId, lastConnectedMachineId, setWebrtcConnecting, setWebrtcReady } = useMachineStore.getState();
      const machineId = selectedMachineId || lastConnectedMachineId;
      if (!machineId) {
        navigate('/');
        return;
      }
      try {
        setWebrtcConnecting(true);
        const { peerConnection } = await import('../services/peerConnection');
        const connected = await peerConnection.connect(machineId);
        if (connected) {
          const { switchToWebRTCBackend } = await import('../services/backend');
          switchToWebRTCBackend();
          setWebrtcReady(true);
          await useProjectStore.getState().loadProjects();
        }
      } catch (error) {
        logger.error('DockingWorkspace', 'Retry connection failed:', error);
      } finally {
        useMachineStore.getState().setWebrtcConnecting(false);
      }
    };
    return (
      <div className="h-full flex items-center justify-center bg-base-100">
        <div className="text-center space-y-4">
          <div>
            <p className="text-lg font-medium">Not connected to a machine</p>
            <p className="text-sm text-base-content/60 mt-1">
              The remote machine may be offline. It will auto-reconnect when available.
            </p>
          </div>
          <div className="flex gap-2 justify-center">
            <button onClick={handleRetry} className="btn btn-primary">Retry Connection</button>
            <button onClick={() => navigate('/')} className="btn btn-ghost">Go Home</button>
          </div>
        </div>
      </div>
    );
  }

  if (!everReadyRef.current && (isLoading || (!project && !isConnectionReady))) {
    // Get project name from persisted store for display while connecting
    const storedProject = useProjectStore.getState().currentProject;
    const stuckOnConnecting = !isConnectionReady; // i.e. WebRTC handshake / project load
    const handleAbort = async () => {
      // Break out of the auto-reconnect loop. peerConnection.disconnect()
      // sets intentionalDisconnect = true (prevents further attempts),
      // clears lastConnectedMachineId (prevents visibility-change retries),
      // and tears down the current connection. Without this the user has
      // no way to escape the spinner short of force-quitting the app.
      try {
        const { peerConnection } = await import('../services/peerConnection');
        peerConnection.disconnect();
        useMachineStore.getState().selectMachine(null);
        useMachineStore.getState().clearLastConnectedMachine();
        useMachineStore.getState().setWebrtcConnecting(false);
        useMachineStore.getState().setWebrtcReady(false);
      } catch (error) {
        logger.error('DockingWorkspace', 'Cancel reconnect failed:', error);
      }
      navigate('/');
    };
    return (
      <div className="h-full flex items-center justify-center bg-base-100">
        <div className="text-center space-y-4">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto" />
          <div>
            <p className="text-lg font-medium">
              {isConnectionReady ? 'Loading project...' : 'Connecting...'}
            </p>
            {storedProject?.name && (
              <p className="text-sm text-base-content/60 mt-1">
                Restoring {storedProject.name}
              </p>
            )}
          </div>
          {stuckOnConnecting && (
            <div className="pt-2">
              <button onClick={handleAbort} className="btn btn-ghost btn-sm">
                Cancel & switch machine
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="h-full flex items-center justify-center bg-base-100">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Project Not Found</h2>
          <p className="text-base-content/70 mb-6">The project you're looking for doesn't exist.</p>
          <button onClick={() => navigate('/')} className="btn btn-primary">Go Home</button>
        </div>
      </div>
    );
  }

  // We have a project and are committed to rendering the workspace — remember
  // it so future project switches keep the Layout mounted (panels swap their
  // projectId in place) instead of unmounting + resetting every host browser.
  everReadyRef.current = true;

  // Mobile: simple tab fallback
  if (isMobile) {
    return <MobileWorkspace projectId={id} project={projectForTabs} />;
  }

  const addableTabs = getAddableTabs();

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Workspace Header */}
      <div className="flex items-center justify-between px-4 py-1.5 bg-base-200 border-b border-base-300 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          {project.icon?.startsWith('data:') ? (
            <img src={project.icon} alt="" className="w-5 h-5 rounded object-cover flex-shrink-0" />
          ) : project.icon ? (
            <span className="text-sm flex-shrink-0">{project.icon}</span>
          ) : null}
          <h1 className="text-sm font-semibold truncate">{project.name}</h1>
          {project.agent_type && (
            <div className="badge badge-primary badge-sm gap-1">
              <Bot className="w-3 h-3" />
              <span className="hidden sm:inline">{project.agent_type}</span>
            </div>
          )}
          <RunButton projectPath={project.root_path} compact />
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Add panel dropdown */}
          {/* The menu is React DOM, but embedded OSR browsers (chat/browser
              panels) composite ABOVE the DOM — so without this the dropdown
              opens behind them. Hide embedded browsers while the menu is open
              via the shared overlayDepth ref-count (same pattern modals use).
              relatedTarget guards keep push/pop balanced across child focus. */}
          <div
            className="dropdown dropdown-end"
            onFocus={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) useUIStore.getState().pushOverlay();
            }}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) useUIStore.getState().popOverlay();
            }}
          >
            <div tabIndex={0} role="button" className="btn btn-ghost btn-xs gap-1" title="Add panel">
              <PanelRight className="w-3.5 h-3.5" />
              <span className="hidden sm:inline text-xs">Add Panel</span>
            </div>
            <ul tabIndex={0} className="dropdown-content z-50 mt-2 p-1 shadow-lg bg-base-200 border border-base-300 rounded-lg w-48">
              {addableTabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <li key={tab.id}>
                    <button onClick={() => addPanel(tab.id, tab.displayName)} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-base-300 rounded-md whitespace-nowrap">
                      <Icon className="w-4 h-4" /> {tab.displayName}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
          {/* Settings */}
          <button onClick={openSettings} className="btn btn-ghost btn-xs" title="Settings">
            <Settings className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* FlexLayout docking area */}
      <div className="flex-1 relative overflow-hidden">
        <Layout
          model={modelRef.current}
          factory={factory}
          onModelChange={handleModelChange}
          onRenderTab={onRenderTab}
        />
      </div>
    </div>
  );
}
